import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { effectiveUiSetting, UI_FIELD_BY_KEY, type UiSettingRow } from '@shared/uiSettings'
import { uiSettingsService } from '@/services/uiSettingsService'
import { useAuth } from '@/stores/auth'

export const uiSettingsKey = ['ui-settings'] as const

/** The raw rule table (small, cached for the session; invalidated after every save). */
export function useUiSettingRows() {
  const authed = useAuth((s) => s.status === 'authenticated')
  return useQuery({ queryKey: uiSettingsKey, queryFn: ({ signal }) => uiSettingsService.list(signal), enabled: authed, staleTime: 5 * 60_000 })
}

function useRowMap(): Map<string, UiSettingRow> {
  const { data } = useUiSettingRows()
  return useMemo(() => new Map((data ?? []).map((r) => [r.key, r])), [data])
}

/** `isVisible(pageId)` for the signed-in user — used by the sidebar and page guards. */
export function usePageVisibility() {
  const rows = useRowMap()
  const username = useAuth((s) => s.user?.username ?? null)
  // a page given «مسموح» to this user (group or himself) beats the global table — صلاحيات المستخدمين decides it
  const allowed = useAuth((s) => s.user?.allowedPages)
  return useCallback((pageId: string) => Boolean(allowed?.includes(pageId)) || effectiveUiSetting(rows.get(pageId), username).visible, [rows, username, allowed])
}

/**
 * Field rules for one form: `show(name)` / `req(name)` where `name` is the part after the form key
 * (e.g. form `shipments.create`, name `contents`). Fields not in the registry are always shown.
 */
export function useFormFields(form: string) {
  const rows = useRowMap()
  const username = useAuth((s) => s.user?.username ?? null)
  return useMemo(() => {
    const of = (name: string) => {
      const key = `${form}.${name}`
      return effectiveUiSetting(rows.get(key), username, UI_FIELD_BY_KEY[key]?.defaultRequired ?? false)
    }
    return {
      show: (name: string) => of(name).visible,
      req: (name: string) => of(name).visible && of(name).required,
    }
  }, [rows, username, form])
}
